import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BookMarked, TrendingUp, Clock, AlertCircle, Zap } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Link } from 'react-router-dom';
import type { TBRAnalyticsStats } from '@/lib/tbrAnalyticsUtils';

interface TBRAnalyticsProps {
  stats: TBRAnalyticsStats;
}

export function TBRAnalytics({ stats }: TBRAnalyticsProps) {
  const {
    totalTBRBooks,
    totalReadBooks,
    conversionRate,
    averageTBRDays,
    categoryPerformance,
    stuckBooks,
    fastestReads,
  } = stats;

  return (
    <div className="space-y-6">
      {/* Overview Card */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookMarked className="w-5 h-5 text-primary" />
            TBR Analytics
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">
            How well you're tackling your To Be Read pile
          </p>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-muted/30 rounded-lg">
              <div className="text-2xl font-bold text-primary">{totalTBRBooks}</div>
              <p className="text-xs text-muted-foreground mt-1">Current TBR</p>
            </div>
            <div className="text-center p-4 bg-muted/30 rounded-lg">
              <div className="text-2xl font-bold text-secondary">{totalReadBooks}</div>
              <p className="text-xs text-muted-foreground mt-1">Read This Year</p>
            </div>
            <div className="text-center p-4 bg-muted/30 rounded-lg">
              <div className="text-2xl font-bold text-accent">{conversionRate}%</div>
              <p className="text-xs text-muted-foreground mt-1">Conversion Rate</p>
            </div>
            <div className="text-center p-4 bg-muted/30 rounded-lg">
              <div className="text-2xl font-bold text-star">{averageTBRDays}</div>
              <p className="text-xs text-muted-foreground mt-1">Avg Days on TBR</p>
            </div>
          </div>

          {/* Conversion Rate Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">TBR Completion Efficiency</span>
              <span className="text-muted-foreground">{conversionRate}%</span>
            </div>
            <Progress value={conversionRate} className="h-2" />
            <p className="text-xs text-muted-foreground">
              {conversionRate >= 60 ? (
                'Great job! You\'re reading through your TBR efficiently'
              ) : conversionRate >= 40 ? (
                'You\'re making steady progress on your TBR'
              ) : (
                'Your TBR is growing - consider prioritizing some reads'
              )}
            </p>
          </div>
        </CardContent>
      </Card>


      {/* Fastest Reads */}
      {fastestReads.length > 0 && (
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              Quick Wins
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Books you read within 30 days of adding to TBR
            </p>
            
            <div className="space-y-3">
              {fastestReads.map((book, index) => (
                <div key={index} className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{book.title}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {book.authors.join(', ')}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <Badge variant="default" className="text-xs">
                      {book.daysOnTBR} day{book.daysOnTBR !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stuck Books */}
      {stuckBooks.length > 0 && (
        <Card className="card-elevated border-amber-500/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Books Waiting 6+ Months
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              These books have been patiently waiting on your TBR
            </p>
            
            <div className="space-y-4">
              {stuckBooks.slice(0, 8).map((book) => (
                <Link 
                  key={book.id}
                  to={`/book/${book.bookId}`}
                  className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors"
                >
                  {book.coverUrl && (
                    <img 
                      src={book.coverUrl}
                      alt={book.title}
                      className="w-12 h-16 object-cover rounded shadow-sm flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm line-clamp-2">{book.title}</p>
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {book.authors.join(', ')}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      {book.category && (
                        <Badge variant="secondary" className="text-xs">
                          {book.category}
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs text-amber-600">
                        <Clock className="w-3 h-3 mr-1" />
                        {Math.round(book.daysOnTBR / 30)} months
                      </Badge>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {stuckBooks.length > 8 && (
              <p className="text-xs text-center text-muted-foreground mt-4">
                + {stuckBooks.length - 8} more books waiting
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
